import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Tips from './dialogs/Tips';

interface AnimeScoresSectionProps {
  malScore: number | null;
  malScoredBy: number | null;
  tmdbVoteAverage: number | null;
  tmdbVoteCount: number | null;
}

const AnimeScoresSection: React.FC<AnimeScoresSectionProps> = ({
  malScore,
  malScoredBy,
  tmdbVoteAverage,
  tmdbVoteCount,
}) => {
  const getScoreColor = (score: number | null, maxScore: number = 100): string => {
    if (score === null) return 'rgba(255, 255, 255, 0.7)';
    const normalizedScore = maxScore === 10 ? score * 10 : score;
    if (normalizedScore >= 80) return '#66cc33';
    if (normalizedScore >= 50) return '#ffcc33';
    return '#ff6666';
  };

  const formatCount = (count: number | null): string => {
    if (count === null || count === undefined) return '-';
    if (count <= 10) return '<10';
    if (count >= 1000) {
      const rounded = Math.floor(count / 100) * 100;
      return `${rounded.toLocaleString()}+`;
    } else {
      const rounded = Math.floor(count / 10) * 10;
      return `${rounded}+`;
    }
  };

  // MAL: 10点満点 → 100点満点に変換
  const malScoreConverted = malScore !== null ? malScore * 10 : null;
  // TMDB: 10点満点 → 100点満点に変換
  const tmdbScoreConverted = tmdbVoteAverage !== null ? tmdbVoteAverage * 10 : null;

  const hasMAL = malScore !== null;
  const hasTMDB = tmdbVoteAverage !== null;

  if (!hasMAL && !hasTMDB) return null;

  const totalReviewCount = (malScoredBy || 0) + (tmdbVoteCount || 0);

  const cardSx = {
    p: { xs: 1, sm: 1.5 },
    borderRadius: 2,
    bgcolor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  };

  return (
    <Box sx={{ mt: { xs: 2, sm: 2 }, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="h2" gutterBottom sx={{ mb: 0, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
          評価スコア
        </Typography>
        {totalReviewCount > 0 && (
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '0.7rem',
              padding: '2px 8px',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {formatCount(totalReviewCount)}
          </Box>
        )}
        <Tips
          title="スコアについて"
          content={
            <Box>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                <strong>PULSE</strong>: PULSEコミュニティによる評価
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                <strong>MAL</strong>: MyAnimeListコミュニティによる評価
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 0.5 }}>
                <strong>TMDB</strong>: The Movie Databaseコミュニティによる評価
              </Typography>
            </Box>
          }
        />
      </Box>
      <Grid container spacing={1}>
        {/* PULSE */}
        <Grid item xs={4}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                PULSE
              </Typography>
              <Chip
                label="JP"
                size="small"
                sx={{
                  height: '14px',
                  fontSize: '0.6rem',
                  fontWeight: 400,
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '3px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.75, py: 0 },
                }}
              />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: '600',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: 'rgba(255, 255, 255, 0.4)',
            }}>
              -
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                color: 'rgba(255, 255, 255, 0.3)',
                fontStyle: 'italic',
              }}>
                Coming Soon
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* MAL */}
        <Grid item xs={4}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                MAL
              </Typography>
              <Chip
                label="GL"
                size="small"
                variant="outlined"
                sx={{
                  height: '14px',
                  fontSize: '0.6rem',
                  fontWeight: 400,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '3px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.75, py: 0 },
                }}
              />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: '600',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: malScore === null
                ? 'rgba(255, 255, 255, 0.4)'
                : ((malScoredBy ?? 0) <= 10 ? 'rgba(255, 255, 255, 0.4)' : getScoreColor(malScoreConverted)),
            }}>
              {malScoreConverted !== null ? Math.round(malScoreConverted) : '-'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
              <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {formatCount(malScoredBy)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* TMDB */}
        <Grid item xs={4}>
          <Box sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                TMDB
              </Typography>
              <Chip
                label="GL"
                size="small"
                variant="outlined"
                sx={{
                  height: '14px',
                  fontSize: '0.6rem',
                  fontWeight: 400,
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: '3px',
                  display: { xs: 'none', sm: 'inline-flex' },
                  '& .MuiChip-label': { px: 0.75, py: 0 },
                }}
              />
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: '600',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: tmdbVoteAverage === null
                ? 'rgba(255, 255, 255, 0.4)'
                : ((tmdbVoteCount ?? 0) <= 10 ? 'rgba(255, 255, 255, 0.4)' : getScoreColor(tmdbScoreConverted)),
            }}>
              {tmdbScoreConverted !== null ? Math.round(tmdbScoreConverted) : '-'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
              <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {formatCount(tmdbVoteCount ?? null)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnimeScoresSection;
